import { useEffect, useState } from 'react';
import useApp from 'hooks/useApp';
import classNames from 'utilities/ClassNames';
import Loader from 'components/shared/Loader';
import GuidesTable from 'components/guides/GuidesTable';
import GuidesTabs from 'components/guides/GuidesTabs';
import GuideModal from 'components/guides/GuideModal';
import CategoryTable from 'components/guides/CategoryTable';
import { getGuideCategories, postGuideCategory, editGuideCategory, removeGuideCategory, postGuide, editGuide, removeGuide } from 'api/guides';

interface Guide {
  id: string;
  categoryId: string;
  name: string;
  desc: string;
  link: string;
}

interface Category {
  id: string;
  name: string;
  guides?: Guide[];
}

interface ApiResponse {
  warnings: string[];
  error: string[];
  list: Category[];
}

export default function Guides() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { isScaled } = useApp();
  const [activeTab, setActiveTab] = useState('guides');
  const [editingGuide, setEditingGuide] = useState<Guide | Category | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  
  // Global state for guides and categories
  const [categories, setCategories] = useState<Category[]>([]);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch categories and guides
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getGuideCategories();
      if (response.status === 200) {
        const data: ApiResponse = response.data;
        setCategories(data.list);
        
        // Flatten guides from all categories
        const allGuides = data.list.reduce((acc: Guide[], category) => {
          if (category.guides) {
            return [...acc, ...category.guides];
          }
          return acc;
        }, []);
        setGuides(allGuides);
      }
    } catch (error) {
      setError(true);
      console.error('Error fetching guides data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle form submissions for both guides and categories
  const handleSubmit = async (formData: any, type: string) => {
    try {
      if (type === 'categories') {
        if (mode === 'create') {
          const response = await postGuideCategory({ name: formData.name });
          if (response.status === 200) {
            fetchData(); // Refresh data after successful creation
          }
        } else {
          const response = await editGuideCategory({
            id: (editingGuide as Category).id,
            name: formData.name
          });
          if (response.status === 200) {
            fetchData(); // Refresh data after successful edit
          }
        }
      } else {
        // Handle guide submission
        const guideData = {
          categoryId: formData.category,
          name: formData.name,
          desc: formData.videoDescription,
          link: formData.videoTitle // Using videoTitle field for link
        };

        if (mode === 'create') {
          const response = await postGuide(guideData);
          if (response.status === 200) {
            fetchData();
          }
        } else {
          const response = await editGuide({
            id: (editingGuide as Guide).id,
            ...guideData
          });
          if (response.status === 200) {
            fetchData();
          }
        }
      }
      setIsModalOpen(false);
      setEditingGuide(null);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // Handle deletion
  const handleDelete = async (id: string, type: 'guides' | 'categories') => {
    try {
      if (type === 'categories') {
        const response = await removeGuideCategory({ id });
        if (response.status === 200) {
          fetchData();
        }
      } else {
        const response = await removeGuide({ id });
        if (response.status === 200) {
          fetchData();
        }
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  // Updated search functionality
  const filteredItems = activeTab === 'guides' 
    ? guides.filter(guide => 
        guide.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guide.link.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guide.desc.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : categories.filter(category => 
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <>
      {loading && (
        <div className="w-full h-[80vh] grid place-content-center">
          <Loader />
        </div>
      )}
      {error && (
        <div className="w-full h-[80vh] grid place-content-center">
          <p>Error loading data</p>
        </div>
      )}
      {!loading && !error && (
        <div className={classNames(
          isScaled ? "text-base" : "text-base",
          "px-6 pb-3 overflow-hidden overflow-y-auto h-full"
        )}>
          <GuidesTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onAddNew={() => {
              setMode('create');
              setEditingGuide(null);
              setIsModalOpen(true);
            }}
            searchTerm={searchTerm}
            onSearch={handleSearch}
          >
            {activeTab === 'guides' ? (
              <GuidesTable
                guides={filteredItems as Guide[]}
                categories={categories}
                onEdit={(guide) => {
                  setMode('edit');
                  setEditingGuide(guide);
                  setIsModalOpen(true);
                }}
                onDelete={(id) => handleDelete(id, 'guides')}
              />
            ) : (
              <CategoryTable
                categories={filteredItems as Category[]}
                onEdit={(category: any) => {
                  setMode('edit');
                  setEditingGuide(category);
                  setIsModalOpen(true);
                }}
                onDelete={(id: string) => handleDelete(id, 'categories')}
              />
            )}
          </GuidesTabs>
        </div>
      )}

      <GuideModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingGuide(null);
        }}
        mode={mode}
        initialData={editingGuide}
        type={activeTab}
        onSubmit={handleSubmit}
        categories={categories}
      />
    </>
  );
} 